import {
  CommentMentionPayload,
  NotificationEventType,
  NotificationPayload,
  OrderCreatedPayload,
  OrderStatusChangedPayload,
  ThreadAssignmentPayload,
  WorkflowSendReplyFailedPayload,
} from '../models/notification.models';
import { UserEvent } from 'src/app/features/smart-inbox/services/realtime.service';
import { NOTIFICATION_EVENTS } from './notification-event-registry';

// ─────────────────────────────────────────────────────────────────────────────
// User Event Adapter
// ─────────────────────────────────────────────────────────────────────────────
// Turns a raw SSE UserEvent into the registry-shaped NotificationPayload, with
// one entry per notification event type and no dispatch on event.type.
// ─────────────────────────────────────────────────────────────────────────────

export interface AdaptedUserEvent {
  eventType: NotificationEventType;
  /** The wl.core.notification id. Cross-tab de-duplication keys on it. */
  notificationId: number;
  payload: NotificationPayload;
}

const NOTIFICATION_EVENT_TYPES = new Set<string>(Object.keys(NOTIFICATION_EVENTS));

/** True for the five event types the notification registry declares. */
export function isNotificationEventType(type: string): type is NotificationEventType {
  return NOTIFICATION_EVENT_TYPES.has(type);
}

type EventOf<T extends NotificationEventType> = Extract<UserEvent, { type: T }>;

const PAYLOAD_ADAPTERS: Record<NotificationEventType, (event: UserEvent) => NotificationPayload> = {
  comment_mention: (event) => {
    const mention = event as EventOf<'comment_mention'>;
    const payload: CommentMentionPayload = {
      thread_id: mention.thread_id,
      comment_id: mention.comment_id,
      author_id: mention.author_id,
      thread_subject: mention.thread_subject ?? null,
      snippet: mention.snippet ?? null,
    };
    return payload;
  },

  thread_assignment: (event) => {
    const assignment = event as EventOf<'thread_assignment'>;
    const payload: ThreadAssignmentPayload = {
      author_id: assignment.author_id,
      thread_ids: assignment.thread_ids,
    };
    return payload;
  },

  order_created: (event) => {
    const created = event as EventOf<'order_created'>;
    const payload: OrderCreatedPayload = {
      thread_id: created.thread_id,
      thread_subject: created.thread_subject ?? null,
    };
    return payload;
  },

  order_status_changed: (event) => {
    const statusChanged = event as EventOf<'order_status_changed'>;
    const payload: OrderStatusChangedPayload = {
      order_id: statusChanged.order_id,
      new_status: statusChanged.new_status,
      email_subject: statusChanged.email_subject ?? null,
      order_name: statusChanged.order_name ?? null,
    };
    return payload;
  },

  workflow_send_reply_failed: (event) => {
    const failure = event as EventOf<'workflow_send_reply_failed'>;
    const payload: WorkflowSendReplyFailedPayload = {
      thread_id: failure.thread_id,
      draft_id: failure.draft_id,
      rule_id: failure.rule_id,
      rule_name: null,
      unresolved_variables: [],
      thread_subject: failure.thread_subject ?? null,
    };
    return payload;
  },
};

/**
 * The registry-shaped payload for an SSE user event, or null when the event is not a
 * notification event or carries no persisted notification.
 */
export function adaptUserEvent(event: UserEvent): AdaptedUserEvent | null {
  if (!isNotificationEventType(event.type)) {
    return null;
  }
  const eventType: NotificationEventType = event.type;
  if (!('notification_id' in event) || !event.notification_id) {
    return null;
  }
  return {
    eventType,
    notificationId: event.notification_id,
    payload: PAYLOAD_ADAPTERS[eventType](event),
  };
}
